import { useQuery } from "@tanstack/react-query";
import rootReducer from "../../../../store";
import {
  setDetailsData,
  setProductVariantModalState,
} from "../../../../slices/orders/orderSlice";
import { updateRendererState } from "../../../../slices/common/reducer";
import { getQuote } from "../QuoteService";

// The route param is a string; the quote API rejects anything the server cannot
// read as a numeric id (a missing id arrives here as "undefined").
export const isValidEntityId = (id) =>
  id !== undefined &&
  id !== null &&
  String(id).trim() !== "" &&
  !Number.isNaN(Number(id));

// "oem" code from variant_json - top level for a single product, inside each
// product's map for a clubbed one (same lookup as the server).
const getOemCode = (variant) => {
  if (!variant) return undefined;
  if (variant.oem) return variant.oem;
  return Object.values(variant).find((product) => product?.oem)?.oem;
};

const parseVariant = (variantJson) => {
  if (typeof variantJson !== "string") return variantJson || undefined;
  try {
    return JSON.parse(variantJson);
  } catch {
    return undefined;
  }
};

// Loads a saved quote into the shared details/form state. Returns the id so
// React Query has defined data to cache.
const useQuoteDetails = (id) =>
  useQuery({
    queryKey: ["quote", "details", id],
    queryFn: async () => {
      const { quote = {}, ...names } = await getQuote(Number(id));
      const details = { ...quote, ...names, entityid: quote.entity_id };
      rootReducer.dispatch(setDetailsData(details));
      rootReducer.dispatch(updateRendererState(details));

      const variant = parseVariant(quote.variant_json);
      if (variant) {
        // The attributes modal reads each product's values at the top level
        // ({ [productId]: {...} }), the quote payload reads them from variant
        rootReducer.dispatch(
          setProductVariantModalState({
            ...variant,
            variant,
            oem: getOemCode(variant),
            finalPremium: quote.gross_amount,
          })
        );
      }
      return id;
    },
    enabled: isValidEntityId(id),
  });

export default useQuoteDetails;
